import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Popover,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Drawer,
} from '@mui/material';
import {
  HelpOutline,
  Close,
  LightbulbOutlined,
} from '@mui/icons-material';

export interface HelpTip {
  text: string;
  icon?: React.ReactNode;
}

export interface PageHelpProps {
  title: string;
  description: string;
  tips?: HelpTip[];
  steps?: string[];
}

const PageHelp: React.FC<PageHelpProps> = ({
  title,
  description,
  tips = [],
  steps = [],
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const helpContent = (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        maxWidth: { xs: '100%', sm: 400 },
        bgcolor: 'rgba(30, 30, 30, 0.98)',
        backdropFilter: 'blur(20px)',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
          {title}
        </Typography>
        <IconButton size="small" onClick={handleClose} sx={{ color: 'text.secondary' }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* Description */}
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: steps.length > 0 || tips.length > 0 ? 2.5 : 0,
          lineHeight: 1.6,
        }}
      >
        {description}
      </Typography>

      {/* How to Use Steps */}
      {steps.length > 0 && (
        <Box sx={{ mb: tips.length > 0 ? 2.5 : 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: '#6366f1', fontWeight: 600, mb: 1.5 }}
          >
            How to use
          </Typography>
          <List dense disablePadding>
            {steps.map((step, index) => (
              <ListItem key={index} disablePadding sx={{ mb: 1 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      bgcolor: 'rgba(99, 102, 241, 0.2)',
                      color: '#6366f1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                    }}
                  >
                    {index + 1}
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={step}
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: 'text.primary',
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Tips */}
      {tips.length > 0 && (
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ color: '#10b981', fontWeight: 600, mb: 1.5 }}
          >
            Tips
          </Typography>
          <List dense disablePadding>
            {tips.map((tip, index) => (
              <ListItem key={index} disablePadding sx={{ mb: 1, alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 28, mt: 0.3 }}>
                  {tip.icon || <LightbulbOutlined sx={{ fontSize: 18, color: '#10b981' }} />}
                </ListItemIcon>
                <ListItemText
                  primary={tip.text}
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: 'text.secondary',
                    lineHeight: 1.5,
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Got it Button */}
      <Button
        fullWidth
        variant="contained"
        onClick={handleClose}
        sx={{
          mt: 2.5,
          py: 1,
          bgcolor: 'rgba(99, 102, 241, 0.15)',
          color: '#6366f1',
          '&:hover': {
            bgcolor: 'rgba(99, 102, 241, 0.25)',
          },
        }}
      >
        Got it
      </Button>
    </Box>
  );

  return (
    <>
      <IconButton
        onClick={handleOpen}
        size="small"
        sx={{
          ml: 1,
          color: 'text.secondary',
          bgcolor: 'rgba(255, 255, 255, 0.05)',
          '&:hover': {
            bgcolor: 'rgba(99, 102, 241, 0.15)',
            color: '#6366f1',
          },
          transition: 'all 0.2s ease',
        }}
      >
        <HelpOutline sx={{ fontSize: 20 }} />
      </IconButton>

      {/* Mobile: Use Drawer, Desktop: Use Popover */}
      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: '80vh',
              bgcolor: 'transparent',
            },
          }}
        >
          {helpContent}
        </Drawer>
      ) : (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 2,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
              overflow: 'hidden',
            },
          }}
        >
          {helpContent}
        </Popover>
      )}
    </>
  );
};

export default PageHelp;
